/**
 * useNicheFinderChannels — busca canais com infinite scroll + SWR.
 *
 * GET /api/nichos-virais/channels?filter=X&limit=N&offset=N&sort_by=Y
 *   + filtros dinâmicos (Pacote 7.5): search, languages[], min/max_subscribers,
 *     min/max_views, monetized_only, added_after.
 *
 * Estado interno: { channels[], offset, hasMore, loading, error }.
 * - Quando a IDENTIDADE da query (filter, sortBy, limit OU qualquer filtro)
 *   muda, reseta tudo e busca a 1a pagina.
 * - `loadMore()` busca a proxima pagina e da APPEND no array existente.
 * - `hasMore` vira false quando o backend retorna menos canais que LIMIT.
 *
 * STALE-WHILE-REVALIDATE: em cache hit, mostra o cache IMEDIATAMENTE (sem
 * loading) e, se passou de REVALIDATE_AFTER_MS, dispara re-fetch em background.
 *
 * Pacote 5 (fix travamento ao trocar aba): toda request em voo é cancelada via
 * AbortController quando a query muda, no unmount, ou quando uma nova request
 * começa.
 *
 * Pacote 7.5:
 * - Todos os filtros agora vão pro BACKEND (antes search/views só rodavam
 *   client-side sobre os 30 carregados → busca não achava canais fora da
 *   página). A cache key inclui os filtros (senão dois estados de filtro
 *   compartilhariam cache → resultado errado).
 * - Reset feito em useLayoutEffect (não useEffect): roda ANTES do paint, então
 *   os cards da aba anterior nunca chegam a ser pintados ao trocar de aba
 *   (fix do "cache visual"). Com useEffect o reset rodava pós-paint e 1 frame
 *   exibia os cards antigos.
 *
 * Param `limit`: tamanho da pagina (default 30).
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';
const DEFAULT_PAGE_SIZE = 30;
const DEFAULT_SORT = 'most_views';

// Janelas relativas do filtro "Adicionado em" → ms. Convertidas pra ISO só na
// hora de montar a URL (a cache key usa o bucket, que é estável).
const ADDED_AFTER_MS = {
  last_hour: 60 * 60 * 1000,
  last_24h: 24 * 60 * 60 * 1000,
  last_7d: 7 * 24 * 60 * 60 * 1000,
  last_30d: 30 * 24 * 60 * 60 * 1000,
};

function _addedAfterToISO(bucket) {
  const ms = ADDED_AFTER_MS[bucket];
  return ms ? new Date(Date.now() - ms).toISOString() : null;
}

// Cache em memória de módulo — compartilhado entre instâncias do hook.
// Chave: `${filter}|${sortBy}|${limit}|${filtersKey}`. TTL 60s; revalida 30s.
const _channelsCache = new Map();
const CHANNELS_CACHE_TTL_MS = 60_000;
const REVALIDATE_AFTER_MS = 30_000;

function _cacheKey(filter, sortBy, limit, filtersKey) {
  return `${filter}|${sortBy}|${limit}|${filtersKey}`;
}

function _getCachedPage(filter, sortBy, limit, filtersKey) {
  const entry = _channelsCache.get(_cacheKey(filter, sortBy, limit, filtersKey));
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > CHANNELS_CACHE_TTL_MS) {
    _channelsCache.delete(_cacheKey(filter, sortBy, limit, filtersKey));
    return null;
  }
  return entry;
}

function _setCachedPage(filter, sortBy, limit, filtersKey, channels, total) {
  _channelsCache.set(_cacheKey(filter, sortBy, limit, filtersKey), {
    channels,
    total,
    cachedAt: Date.now(),
  });
}

function _refreshCacheTimestamp(filter, sortBy, limit, filtersKey) {
  const entry = _channelsCache.get(_cacheKey(filter, sortBy, limit, filtersKey));
  if (entry) entry.cachedAt = Date.now();
}

function _idsSignature(list) {
  return (list || []).map((c) => c.channel_id).sort().join(',');
}

function _isCancel(err) {
  return (typeof axios.isCancel === 'function' && axios.isCancel(err))
    || err?.code === 'ERR_CANCELED'
    || err?.name === 'CanceledError'
    || err?.name === 'AbortError';
}

export function useNicheFinderChannels({
  filter,
  sortBy = DEFAULT_SORT,
  enabled = true,
  limit = DEFAULT_PAGE_SIZE,
  // Filtros dinâmicos (Pacote 7.5) — todos opcionais. Repassados ao backend.
  search = '',
  languages = [],
  subsMin = null,
  subsMax = null,
  viewsMin = null,
  viewsMax = null,
  videosMin = null,
  videosMax = null,
  monetized = null,
  addedAfter = 'any',
}) {
  const [channels, setChannels] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Controller da request em voo. Abortado em: troca de query, unmount, ou
  // início de uma nova request (fetchPage / loadMore / revalidate).
  const abortRef = useRef(null);

  // Assinatura estável dos filtros — usada na cache key E nas deps do effect.
  // addedAfter entra como bucket ('last_24h'), não como timestamp, pra não
  // mudar a cada render.
  const filtersKey = JSON.stringify({
    search: search || '',
    languages: Array.isArray(languages) ? languages : [],
    subsMin: subsMin ?? null,
    subsMax: subsMax ?? null,
    viewsMin: viewsMin ?? null,
    viewsMax: viewsMax ?? null,
    videosMin: videosMin ?? null,
    videosMax: videosMax ?? null,
    monetized: monetized ?? null,
    addedAfter: addedAfter || 'any',
  });

  const buildUrl = useCallback((pageOffset) => {
    const params = new URLSearchParams();
    params.set('filter', filter);
    params.set('limit', String(limit));
    params.set('offset', String(pageOffset));
    params.set('sort_by', sortBy || DEFAULT_SORT);
    if (search) params.set('search', search);
    (Array.isArray(languages) ? languages : []).forEach((l) => {
      if (l) params.append('languages', l);
    });
    if (subsMin != null) params.set('min_subscribers', String(subsMin));
    if (subsMax != null) params.set('max_subscribers', String(subsMax));
    if (viewsMin != null) params.set('min_views', String(viewsMin));
    if (viewsMax != null) params.set('max_views', String(viewsMax));
    if (videosMin != null) params.set('min_videos', String(videosMin));
    if (videosMax != null) params.set('max_videos', String(videosMax));
    if (monetized === true) params.set('monetized_only', 'true');
    const addedISO = _addedAfterToISO(addedAfter);
    if (addedISO) params.set('added_after', addedISO);
    return `${API}/nichos-virais/channels?${params.toString()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortBy, limit, filtersKey]);

  const fetchPage = useCallback(async (pageOffset, reset) => {
    if (!enabled || !filter) return;
    // Aborta qualquer request anterior ainda em voo.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(buildUrl(pageOffset), {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const fetched = data.channels || [];
      const fetchedTotal = data.total || 0;
      const loadedSoFar = pageOffset + fetched.length;
      setTotal(fetchedTotal);
      setChannels((prev) => (reset ? fetched : [...prev, ...fetched]));
      setOffset(loadedSoFar);
      setHasMore(fetched.length === limit && loadedSoFar < fetchedTotal);
      // Cacheia SO a 1a pagina (reset). loadMore nunca cacheia.
      if (reset) _setCachedPage(filter, sortBy, limit, filtersKey, fetched, fetchedTotal);
    } catch (err) {
      if (_isCancel(err)) return; // cancelada — ignora silenciosamente
      setError(err);
      if (reset) {
        setChannels([]);
        setTotal(0);
      }
      setHasMore(false);
    } finally {
      // So mexe no loading se ESTA controller ainda for a atual — senao uma
      // request mais nova ja assumiu o controle.
      if (abortRef.current === controller) {
        setLoading(false);
        abortRef.current = null;
      }
    }
  }, [filter, sortBy, enabled, limit, filtersKey, buildUrl]);

  // SWR: revalida a 1a pagina em background (sem loading). So atualiza
  // state/cache se mudou (channel_ids ou total). Compartilha o abortRef —
  // troca de aba / loadMore cancela esta revalidacao.
  const revalidateInBackground = useCallback(async (cached) => {
    if (!enabled || !filter) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(buildUrl(0), {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const fresh = data.channels || [];
      const freshTotal = data.total || 0;
      if (_idsSignature(fresh) !== _idsSignature(cached.channels) || freshTotal !== cached.total) {
        setChannels(fresh);
        setTotal(freshTotal);
        setOffset(fresh.length);
        setHasMore(fresh.length === limit && fresh.length < freshTotal);
        _setCachedPage(filter, sortBy, limit, filtersKey, fresh, freshTotal);
      } else {
        _refreshCacheTimestamp(filter, sortBy, limit, filtersKey);
      }
    } catch (err) {
      if (!_isCancel(err)) {
        // eslint-disable-next-line no-console
        console.warn('Niche Finder background revalidation failed:', err);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [filter, sortBy, enabled, limit, filtersKey, buildUrl]);

  // Reset + 1a pagina quando a IDENTIDADE da query muda. useLayoutEffect (não
  // useEffect) pra que o reset/cache aconteça ANTES do browser pintar — sem
  // isso, 1 frame exibia os cards da aba anterior (bug do "cache visual"). O
  // cleanup ABORTA a request em voo (corrige o travamento ao trocar de aba).
  useLayoutEffect(() => {
    if (!enabled || !filter) {
      setChannels([]);
      setTotal(0);
      setOffset(0);
      setHasMore(false);
      setLoading(false);
      return undefined;
    }
    setChannels([]);
    setOffset(0);
    setHasMore(true);

    const cached = _getCachedPage(filter, sortBy, limit, filtersKey);
    if (cached) {
      // Stale-while-revalidate: mostra o cache imediatamente...
      setChannels(cached.channels);
      setTotal(cached.total);
      setOffset(cached.channels.length);
      setHasMore(cached.channels.length === limit && cached.channels.length < cached.total);
      setLoading(false);
      // ...e revalida em background se o cache já envelheceu.
      if (Date.now() - cached.cachedAt > REVALIDATE_AFTER_MS) {
        revalidateInBackground(cached);
      }
    } else {
      fetchPage(0, true);
    }

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortBy, enabled, limit, filtersKey]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    fetchPage(offset, false);
  }, [loading, hasMore, offset, fetchPage]);

  const refetch = useCallback(() => {
    setChannels([]);
    setOffset(0);
    setHasMore(true);
    fetchPage(0, true);
  }, [fetchPage]);

  return { channels, total, loading, error, refetch, loadMore, hasMore };
}
